import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Checkbox,
  DatePicker,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "../../../../components";
import {
  GrupoServicoService,
  PessoaService,
  RotaService,
  ServicoService,
  SessionService,
} from "../../../../services";
import { value } from "../../../../helpers";
import { responseErros, timeoutBuscaAutocomplete } from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";

const pessoaService = new PessoaService();
const grupoServicoService = new GrupoServicoService();
const rotaService = new RotaService();
const servicoService = new ServicoService();

const FormFiltroAvancadoServicosVendidos = ({
  filtrosAvancados,
  setFiltrosAvancados,
  filtrosAplicadosImpressao,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const inputRefCliente = useRef();
  const inputRefServico = useRef();

  const [clienteList, setClienteList] = useState([]);
  const [servicoList, setServicoList] = useState([]);
  const [grupoServicoList, setGrupoServicoList] = useState([]);
  const [rotaList, setRotaList] = useState([]);
  const [prestadorList, setPrestadorList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);

  const { onChangeAutocomplete, onChangeDate, onChangeCheckbox } =
    useFiltrosRelatoriosHandlers(
      setFiltrosAvancados,
      setFiltrosAplicadosImpressao,
    );

  const sessionService = new SessionService();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();

  useEffect(() => {
    setEstadoInicialFiltros();
    buscarGruposServico();
    buscarRotas();
    buscarPrestadores();
  }, []);

  function setEstadoInicialFiltros() {
    const filtros = {
      ...filtrosAvancados,
      empresasIds: [empresa.id],
      situacaoOs: "AMBAS",
      criterioOrdem: "MAIS_VENDIDO",
      considerarProdutos: true,
    };
    let filtrosImpressao = {};
    if (usuario?.acessosEmpresas?.length) {
      filtrosImpressao = {
        ...filtrosAplicadosImpressao,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      };
    }
    setFiltrosAvancados(filtros);
    setFiltrosAplicadosImpressao(filtrosImpressao);
  }

  const buscarClientes = (filtros) => {
    const params = {
      nonPaginated: true,
      tiposPessoaId: 1,
      ...filtros,
    };
    pessoaService.getAll(params).then((result) => {
      if (!result.isAxiosError) {
        setClienteList(result.data);
      } else {
        responseErros(result);
      }
    });
    setLoadingAutoComplete(false);
  };

  const buscarServicos = (filtros) => {
    const params = {
      nonPaginated: true,
      ...filtros,
    };
    servicoService.getAll(params).then((result) => {
      if (!result.isAxiosError) {
        setServicoList(result.data?.rows ?? result.data ?? []);
      } else {
        responseErros(result);
      }
    });
    setLoadingAutoComplete(false);
  };

  const buscarGruposServico = () => {
    grupoServicoService.getAll({ nonPaginated: true }).then((result) => {
      if (!result.isAxiosError) {
        setGrupoServicoList(result.data?.rows ?? result.data ?? []);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarRotas = () => {
    rotaService.getAll({ nonPaginated: true }).then((result) => {
      if (!result.isAxiosError) {
        setRotaList(result.data?.rows ?? result.data ?? []);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarPrestadores = () => {
    pessoaService
      .getAll({ nonPaginated: true, tiposPessoaId: 5 })
      .then((result) => {
        if (!result.isAxiosError) {
          setPrestadorList(result.data);
        } else {
          responseErros(result);
        }
      });
  };

  const onInputChangePessoa = (event, tiposPessoaId) => {
    const inputRefMap = {
      1: { inputRef: inputRefCliente, buscar: buscarClientes },
    };
    const { inputRef, buscar } = inputRefMap[tiposPessoaId] || {};
    if (
      event.key === "Enter" &&
      inputRef?.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      const filtros = {
        nomeRazaoSocial: event.target.value,
        apelidoFantasia: event.target.value,
      };
      timeoutBuscaAutocomplete(buscar?.(filtros));
    }
  };

  const onInputChangeServico = (event) => {
    if (
      event.key === "Enter" &&
      inputRefServico?.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      timeoutBuscaAutocomplete(
        buscarServicos({ descricao: event.target.value }),
      );
    }
  };

  function onChangeMultipleEmpresas(empresas) {
    if (!empresas.length) return;
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  function handleChangeSituacaoOs(e) {
    const val = e.target.value;
    setFiltrosAvancados((prev) => ({ ...prev, situacaoOs: val }));
    const labels = { ABERTA: "Aberta", FECHADA: "Fechada", AMBAS: "Ambas" };
    setFiltrosAplicadosImpressao((prev) => ({
      ...prev,
      "Situação-OS": labels[val] ?? val,
    }));
  }

  function handleChangeCriterioOrdem(e) {
    const val = e.target.value;
    setFiltrosAvancados((prev) => ({ ...prev, criterioOrdem: val }));
  }

  return (
    <>
      {usuario?.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : "",
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="servicoId"
              name="Serviço"
              options={servicoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.descricao ?? ""}
              getOptionSelected={(option, val) =>
                option?.descricao === val?.descricao
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Serviço"
                  variant="outlined"
                  onKeyDown={onInputChangeServico}
                  inputRef={inputRefServico}
                  InputProps={{ ...params.InputProps }}
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("servicoId", val, "Serviço")
              }
              value={value.autoComplete(
                servicoList,
                filtrosAvancados?.servicoId,
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="grupoServicoId"
              name="Grupo-Serviço"
              options={grupoServicoList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.descricao ?? ""}
              getOptionSelected={(option, val) =>
                option?.descricao === val?.descricao
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grupo de Serviço"
                  variant="outlined"
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("grupoServicoId", val, "Grupo-Serviço")
              }
              value={value.autoComplete(
                grupoServicoList,
                filtrosAvancados?.grupoServicoId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="clienteId"
              name="Cliente"
              options={clienteList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, val) =>
                option?.nomeRazaoSocial === val?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant="outlined"
                  onKeyDown={(e) => onInputChangePessoa(e, 1)}
                  inputRef={inputRefCliente}
                  InputProps={{ ...params.InputProps }}
                />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("clienteId", val, "Cliente")
              }
              value={value.autoComplete(
                clienteList,
                filtrosAvancados?.clienteId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="rotaId"
              name="Rota"
              options={rotaList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) =>
                option?.descricao ?? option?.nome ?? ""
              }
              getOptionSelected={(option, val) => option?.id === val?.id}
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Rota" variant="outlined" />
              )}
              onChange={(e, val) => onChangeAutocomplete("rotaId", val, "Rota")}
              value={value.autoComplete(rotaList, filtrosAvancados?.rotaId)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="prestadorId"
              name="Prestador"
              options={prestadorList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, val) =>
                option?.nomeRazaoSocial === val?.nomeRazaoSocial
              }
              noOptionsText="Sem opções"
              renderInput={(params) => (
                <TextField {...params} label="Prestador" variant="outlined" />
              )}
              onChange={(e, val) =>
                onChangeAutocomplete("prestadorId", val, "Prestador")
              }
              value={value.autoComplete(
                prestadorList,
                filtrosAvancados?.prestadorId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataAberturaStart"
            name="Data-Abertura-De"
            label="Data Abertura De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataAberturaStart)}
            onChange={(date) =>
              onChangeDate("dataAberturaStart", date, "Data-Abertura-De")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataAberturaEnd"
            name="Data-Abertura-Até"
            label="Data Abertura Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataAberturaEnd)}
            onChange={(date) =>
              onChangeDate("dataAberturaEnd", date, "Data-Abertura-Até")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataFechamentoStart"
            name="Data-Fechamento-De"
            label="Data Fechamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataFechamentoStart)}
            onChange={(date) =>
              onChangeDate("dataFechamentoStart", date, "Data-Fechamento-De")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataFechamentoEnd"
            name="Data-Fechamento-Até"
            label="Data Fechamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataFechamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataFechamentoEnd", date, "Data-Fechamento-Até")
            }
            slotProps={{ popper: { sx: { zIndex: 2000 } } }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl className="d-flex justify-content-center align-items-center p-2">
            <RadioGroup
              row
              aria-labelledby="situacao-os-label"
              name="situacaoOs"
              value={filtrosAvancados?.situacaoOs || "AMBAS"}
              onChange={handleChangeSituacaoOs}
            >
              <FormControlLabel
                value="ABERTA"
                control={<Radio />}
                label="Abertas"
              />
              <FormControlLabel
                value="FECHADA"
                control={<Radio sx={{ ml: "1rem" }} />}
                label="Fechadas"
              />
              <FormControlLabel
                value="AMBAS"
                control={<Radio sx={{ ml: "1rem" }} />}
                label="Ambas"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="criterioOrdem"
            name="criterioOrdem"
            label="Critério de Ordem"
            margin="normal"
            fullWidth
            value={value.text(
              filtrosAvancados?.criterioOrdem || "MAIS_VENDIDO",
            )}
            onChange={handleChangeCriterioOrdem}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: { sx: { zIndex: 1600 } },
              },
            }}
          >
            <MenuItem value="MAIS_VENDIDO">Mais Vendido</MenuItem>
            <MenuItem value="MENOS_VENDIDO">Menos Vendido</MenuItem>
            <MenuItem value="MAIS_RECENTE">Mais Recente</MenuItem>
            <MenuItem value="MAIOR_VALOR_VENDA">
              Com Maior Valor de Venda
            </MenuItem>
            <MenuItem value="MENOR_VALOR_VENDA">
              Com Menor Valor de Venda
            </MenuItem>
            <MenuItem value="DESCRICAO">Descrição</MenuItem>
            <MenuItem value="CODIGO">Código</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormControlLabel
            label="Agrupar por Cliente"
            labelPlacement="end"
            control={
              <Checkbox
                name="agruparPorCliente"
                checked={!!filtrosAvancados?.agruparPorCliente}
                onChange={(e) => onChangeCheckbox(e, "Agrupar-Por-Cliente")}
                color="primary"
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            label="Agrupar por Grupo de Serviço"
            labelPlacement="end"
            control={
              <Checkbox
                name="agruparPorGrupoServico"
                checked={!!filtrosAvancados?.agruparPorGrupoServico}
                onChange={(e) =>
                  onChangeCheckbox(e, "Agrupar-Por-Grupo-Serviço")
                }
                color="primary"
              />
            }
          />
        </Grid>
        {/* <Grid item xs={12}>
          <FormControlLabel
            label="Agrupar por tipo de pagamento"
            labelPlacement="end"
            control={
              <Checkbox
                name="agruparPorTipoPagamento"
                checked={!!filtrosAvancados?.agruparPorTipoPagamento}
                onChange={(e) =>
                  onChangeCheckbox(e, "Agrupar-Por-Tipo-Pagamento")
                }
                color="primary"
              />
            }
          />
        </Grid> */}
        <Grid item xs={6}>
          <FormControlLabel
            label="Apenas o que é gerado para Contas a Receber"
            labelPlacement="end"
            control={
              <Checkbox
                name="apenasContasReceber"
                checked={!!filtrosAvancados?.apenasContasReceber}
                onChange={(e) => onChangeCheckbox(e, "Apenas-Contas-Receber")}
                color="primary"
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            label="Separar por Mês"
            labelPlacement="end"
            control={
              <Checkbox
                name="separarPorMes"
                checked={!!filtrosAvancados?.separarPorMes}
                onChange={(e) => onChangeCheckbox(e, "Separar-Por-Mês")}
                color="primary"
              />
            }
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            label="Considerar Produtos"
            labelPlacement="end"
            control={
              <Checkbox
                name="considerarProdutos"
                checked={
                  !filtrosAvancados?.agruparPorGrupoServico &&
                  !!filtrosAvancados?.considerarProdutos
                }
                disabled={!!filtrosAvancados?.agruparPorGrupoServico}
                onChange={(e) => onChangeCheckbox(e, "Considerar-Produtos")}
                color="primary"
              />
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default FormFiltroAvancadoServicosVendidos;
